/**
 * THE NEWEST REPLIES OF A LONG THREAD, and an honest line when there are more.
 *
 * Bobbie, 2026-09-28, verbatim: "and the messeges? i am hearing people feel
 * like they can see things in notifications, but messages dont' always show
 * everything?" Every board thread used to load its OLDEST 200 replies, so on
 * a thread past 200 the newest replies, the exact ones every bell and push
 * had just announced, were the ones that never showed, and nothing on screen
 * said anything was missing. The class board already did this right (the
 * newest window, and "Showing the newest 100 of N readable replies."); the
 * comp board, the Comp Parent Board, the Jr Staff board and the New Comp
 * Parent Board now follow it.
 *
 * Each board asks Firestore for the window plus ONE: orderBy('createdAt',
 * 'asc') with limitToLast(window + 1). That extra document is the whole
 * "are there earlier replies" answer, and the order it arrives in is already
 * oldest to newest, so nothing is re-sorted. No new index: a single-field
 * orderBy with no filter reads both directions from the automatic index.
 *
 * Pure on purpose (no Firebase, no React) so node --test reaches it, and so
 * the board harnesses, which swap each board's own lib for a mock, still
 * load the real copy of this file.
 */

/** Replies a board thread shows at first, and adds each time she asks for earlier ones. */
export const BOARD_REPLY_WINDOW = 200;

export interface BoardReplyWindow<T> {
  /** Oldest to newest, at most `size` of them. */
  rows: T[];
  /** True when the read held more than `size`: earlier replies exist. */
  hasEarlier: boolean;
}

/**
 * Trim a limitToLast(size + 1) result, oldest first, to its newest `size`.
 * A result of `size` or fewer is the whole thread.
 */
export function newestReplyWindow<T>(oldestFirst: readonly T[], size: number = BOARD_REPLY_WINDOW): BoardReplyWindow<T> {
  const limit = Math.max(1, Math.trunc(size));
  if (oldestFirst.length <= limit) return { rows: [...oldestFirst], hasEarlier: false };
  return { rows: oldestFirst.slice(oldestFirst.length - limit), hasEarlier: true };
}

/**
 * What the thread says above its replies when earlier ones are not shown, or
 * null when the thread is whole. `total` is the post's own stored reply count
 * when the board keeps one; a count that does not exceed what is shown is
 * not trusted to say anything and the plain line is used.
 */
export function boardReplyWindowLabel(
  shown: number,
  hasEarlier: boolean,
  total?: number | null
): string | null {
  if (!hasEarlier) return null;
  const count = typeof total === 'number' && Number.isFinite(total) ? Math.trunc(total) : null;
  return count !== null && count > shown
    ? `Showing the newest ${shown} of ${count.toLocaleString('en-US')} replies.`
    : `Showing the newest ${shown} replies.`;
}

/** The next window after "Show earlier replies". */
export function nextBoardReplyWindow(current: number): number {
  return Math.max(BOARD_REPLY_WINDOW, Math.trunc(current)) + BOARD_REPLY_WINDOW;
}

/** The button under that line. */
export const SHOW_EARLIER_REPLIES_LABEL = 'Show earlier replies';
